import { useRef, useEffect, useState } from 'react';
import { Card, CardBody, Button } from '@heroui/react';
import { Trash2, MapPin, Plus, Check, X, Pencil } from 'lucide-react';
import type { Markup, Area } from '@estimator/shared';
import type { OverrideValues } from '@/store/markupStore';
import { getMarkupColor } from '../../lib/ol-styles';
import { formatMeasurement } from '../../lib/ol-measurement';
import OverrideInput from './OverrideInput';

interface MarkupCardProps {
  markup: Markup;
  index: number;
  isSelected: boolean;
  pendingOverride: OverrideValues | null;
  onOverrideChange: (field: string, value: number | string | null) => void;
  onOverrideClear: () => void;
  onSelect: () => void;
  onHover: (hovering: boolean) => void;
  onDelete: () => void;
  onAreaChange?: (areaId: string | null) => void;
  onLabelChange?: (label: string) => void;
  onNavigateToPage?: (pageId: string) => void;
  pageName?: string;
  areas?: Area[];
  onCreateArea?: (name: string) => void;
  isDeleting?: boolean;
  revisionNumber?: number;
}

function formatDim(val: number | null): string {
  if (val == null) return '--';
  return val.toFixed(1);
}

export default function MarkupCard({
  markup,
  index,
  isSelected,
  pendingOverride,
  onOverrideChange,
  onOverrideClear,
  onSelect,
  onHover,
  onDelete,
  onAreaChange,
  onLabelChange,
  onNavigateToPage,
  pageName,
  areas = [],
  onCreateArea,
  isDeleting = false,
  revisionNumber,
}: MarkupCardProps) {
  const [showAreaPicker, setShowAreaPicker] = useState(false);
  const [newAreaName, setNewAreaName] = useState('');
  const [isCreatingArea, setIsCreatingArea] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const cardRef = useRef<HTMLDivElement>(null);
  const color = markup.color || getMarkupColor(index);

  const unit = markup.ai_unit ?? 'm\u00B2';

  // Auto-scroll into view when selected
  useEffect(() => {
    if (isSelected && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isSelected]);

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
    >
      <Card
        className="w-full transition-all duration-150"
        style={{
          backgroundColor: isSelected ? 'var(--bg-hover)' : 'var(--bg-panel)',
          borderLeft: `3px solid ${color}`,
          borderTop: '1px solid var(--border)',
          borderRight: '1px solid var(--border)',
          borderBottom: '1px solid var(--border)',
          boxShadow: isSelected ? `0 0 0 1px ${color}40` : 'none',
        }}
      >
        <CardBody className="p-0">
          {/* Header row: clickable to select markup on canvas + navigate to page */}
          <div
            className="flex items-start justify-between gap-2 p-3 pb-0 cursor-pointer"
            onClick={() => {
              if (markup.page_id && onNavigateToPage) onNavigateToPage(markup.page_id);
              onSelect();
            }}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* Number badge */}
              <span
                className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-ui font-bold text-white"
                style={{ backgroundColor: color }}
              >
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                {isRenaming && onLabelChange ? (
                  <input
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && renameValue.trim()) {
                        onLabelChange(renameValue.trim());
                        setIsRenaming(false);
                      }
                      if (e.key === 'Escape') setIsRenaming(false);
                    }}
                    onBlur={() => {
                      if (renameValue.trim() && renameValue.trim() !== markup.label) {
                        onLabelChange(renameValue.trim());
                      }
                      setIsRenaming(false);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="text-sm font-medium w-full px-1 py-0 rounded border outline-none"
                    style={{
                      borderColor: 'var(--accent)',
                      backgroundColor: 'var(--bg-base)',
                      color: 'var(--text-primary)',
                    }}
                  />
                ) : (
                  <span
                    className="text-sm font-medium truncate block"
                    style={{ color: 'var(--text-primary)' }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (onLabelChange) {
                        setRenameValue(markup.label);
                        setIsRenaming(true);
                      }
                    }}
                    title="Double-click to rename"
                  >
                    {markup.label}
                  </span>
                )}
                {pageName && (
                  <span className="text-[10px] block" style={{ color: 'var(--text-muted)' }}>
                    {pageName}
                  </span>
                )}
              </div>
            </div>

            {/* Delete button */}
            <Button
              isIconOnly
              size="sm"
              variant="light"
              isLoading={isDeleting}
              onPress={() => { onDelete(); }}
              aria-label="Delete markup"
              className="min-w-6 w-6 h-6 opacity-40 hover:opacity-100 transition-opacity flex-shrink-0"
              style={{ color: 'var(--red, #ef4444)' }}
            >
              <Trash2 size={12} />
            </Button>
          </div>

          {/* Card body — not clickable, interactive elements work freely */}
          <div className="px-3 pb-3 pt-2 space-y-2.5">

          {/* Author identity */}
          {markup.author_name && (
            <div className="flex items-center gap-1.5 mt-1">
              <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                {markup.author_name}
              </span>
              <span
                className="text-[9px] font-medium px-1 py-0.5 rounded"
                style={{
                  backgroundColor: markup.author_role === 'ADMIN'
                    ? 'color-mix(in srgb, var(--blue) 15%, transparent)'
                    : 'color-mix(in srgb, var(--green) 15%, transparent)',
                  color: markup.author_role === 'ADMIN' ? 'var(--blue)' : 'var(--green)',
                }}
              >
                {markup.author_role === 'ADMIN' ? 'Reviewer' : 'Engineer'}
              </span>
              {revisionNumber && (
                <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  Rev {revisionNumber}
                </span>
              )}
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                {new Date(markup.created_at).toLocaleDateString(undefined, {
                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </div>
          )}

          {/* Dimensions row */}
          {(markup.ai_length != null || markup.ai_width != null || markup.ai_height != null) && (
            <div className="flex items-center gap-1">
              <span className="font-ui text-xs" style={{ color: 'var(--text-secondary)' }}>
                {formatDim(markup.ai_length)} &times; {formatDim(markup.ai_width)} &times;{' '}
                {formatDim(markup.ai_height)} m
              </span>
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                (L&times;W&times;H)
              </span>
            </div>
          )}

          {/* Measured value (from geometry) */}
          {markup.measured_value != null && (
            <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {formatMeasurement(
                markup.measured_value,
                markup.measured_unit || 'ft',
                markup.measurement_type?.includes('area') ?? false
              )}
            </div>
          )}

          {/* Area assignment */}
          {onAreaChange && (
            <div
              className="pt-1.5 mt-0.5 border-t"
              style={{ borderColor: 'var(--border)' }}
            >
              {showAreaPicker ? (
                <div className="space-y-1">
                  {/* Existing areas */}
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => { onAreaChange(null); setShowAreaPicker(false); }}
                      className="px-2 py-0.5 rounded-full text-[10px] border transition-colors cursor-pointer"
                      style={{
                        borderColor: !markup.area_id ? 'var(--accent)' : 'var(--border)',
                        backgroundColor: !markup.area_id ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'transparent',
                        color: !markup.area_id ? 'var(--accent)' : 'var(--text-muted)',
                      }}
                    >
                      None
                    </button>
                    {areas.map((a) => (
                      <button
                        key={a.id}
                        onClick={() => { onAreaChange(a.id); setShowAreaPicker(false); }}
                        className="px-2 py-0.5 rounded-full text-[10px] border transition-colors cursor-pointer"
                        style={{
                          borderColor: markup.area_id === a.id ? 'var(--accent)' : 'var(--border)',
                          backgroundColor: markup.area_id === a.id ? 'color-mix(in srgb, var(--accent) 15%, transparent)' : 'transparent',
                          color: markup.area_id === a.id ? 'var(--accent)' : 'var(--text-secondary)',
                        }}
                      >
                        {a.name}
                      </button>
                    ))}
                  </div>
                  {/* Create new area inline */}
                  {onCreateArea && (
                    isCreatingArea ? (
                      <div className="flex items-center gap-1">
                        <input
                          value={newAreaName}
                          onChange={(e) => setNewAreaName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newAreaName.trim()) {
                              onCreateArea(newAreaName.trim());
                              setNewAreaName('');
                              setIsCreatingArea(false);
                            }
                            if (e.key === 'Escape') { setIsCreatingArea(false); setNewAreaName(''); }
                          }}
                          placeholder="Area name..."
                          autoFocus
                          className="flex-1 text-[10px] px-2 py-0.5 rounded border outline-none"
                          style={{
                            borderColor: 'var(--border)',
                            backgroundColor: 'var(--bg-base)',
                            color: 'var(--text-primary)',
                          }}
                        />
                        <button
                          onClick={() => {
                            if (newAreaName.trim()) {
                              onCreateArea(newAreaName.trim());
                              setNewAreaName('');
                              setIsCreatingArea(false);
                            }
                          }}
                          style={{ color: 'var(--green)' }}
                        >
                          <Check size={12} />
                        </button>
                        <button
                          onClick={() => { setIsCreatingArea(false); setNewAreaName(''); }}
                          style={{ color: 'var(--text-muted)' }}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsCreatingArea(true)}
                        className="flex items-center gap-1 text-[10px] cursor-pointer"
                        style={{ color: 'var(--accent)' }}
                      >
                        <Plus size={10} /> New area
                      </button>
                    )
                  )}
                  <button
                    onClick={() => setShowAreaPicker(false)}
                    className="text-[9px] cursor-pointer"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAreaPicker(true)}
                  className="flex items-center gap-1.5 text-[10px] cursor-pointer w-full"
                  style={{ color: markup.area_id ? 'var(--text-secondary)' : 'var(--text-muted)' }}
                >
                  <MapPin size={10} />
                  {markup.area_id
                    ? areas.find((a) => a.id === markup.area_id)?.name ?? 'Unknown area'
                    : 'Assign to area...'}
                </button>
              )}
            </div>
          )}

          {/* Override input section */}
          <div
            className="pt-1.5 mt-0.5 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <OverrideInput
              currentValue={pendingOverride?.area ?? markup.override_area}
              aiValue={markup.ai_area}
              unit={unit}
              onChange={(val) => onOverrideChange('area', val)}
              onClear={onOverrideClear}
            />
          </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
